import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import Avatar from '@mui/material/Avatar';
import { FC, useEffect, useState, MouseEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { logoutRequest } from 'redux/reducers/auth/reducer';
import { Badge, Tooltip, Typography } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import {
	getMessages,
	getNewMessageCount,
	getOrganization,
	getUserInfo,
} from 'redux/reducers/user/selectors';
import {
	getMessagesRequest,
	getNewMessageCountRequest,
	getUserInfoRequest,
} from 'redux/reducers/user/reducer';
import useInterval from 'use-interval';
import { getAuthRefreshToken } from 'redux/reducers/auth/selectors';
import { getUserImage } from 'utils/getUserImage';
import { PATHS } from 'routes/constants';
import { UserMenu } from './UserMenu';
import { MessagesMenu } from './MessagesMenu';
import { PastDueBanner } from './PastDueBanner';
import { NavigationItems } from './NavigationItems';

const ResponsiveAppBar: FC = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();

	const userinfo = useSelector(getUserInfo);
	const refreshToken = useSelector(getAuthRefreshToken);

	useEffect(() => {
		if (!userinfo) dispatch(getUserInfoRequest());
	}, [dispatch, userinfo]);

	useInterval(() => {
		dispatch(getUserInfoRequest());
	}, 20_000);

	const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
	const handleOpenNavMenu = (event: MouseEvent<HTMLElement>) => {
		setAnchorElNav(event.currentTarget);
	};
	const handleCloseNavMenu = () => {
		setAnchorElNav(null);
	};

	const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
	const handleOpenUserMenu = (event: MouseEvent<HTMLElement>) => {
		setAnchorElUser(event.currentTarget);
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-expect-error
		window.HubSpotConversations?.widget?.close();
	};
	const handleCloseUserMenu = () => {
		setAnchorElUser(null);
	};

	const [anchorElMessages, setAnchorElMessages] = useState<null | HTMLElement>(null);
	const handleOpenMessagesMenu = (event: MouseEvent<HTMLElement>) => {
		setAnchorElMessages(event.currentTarget);
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-expect-error
		window.HubSpotConversations?.widget?.close();
	};
	const handleCloseMessagesMenu = () => {
		setAnchorElMessages(null);
	};

	const handleLogout = () => {
		setAnchorElMessages(null);
		if (refreshToken) {
			dispatch(logoutRequest({ refreshToken }));
		}
	};

	const stringAvatar = (name: string) => {
		if (!name) return {};
		const children = name.includes(' ')
			? `${name?.split(' ')?.[0]?.[0]}${name?.split(' ')?.[1]?.[0]}`
			: name?.[0];
		return {
			sx: {
				bgcolor: '#fff',
				height: '30px',
				width: '30px',
				fontSize: '1.0rem',
				borderRadius: '8px',
				color: '#016193',
				fontWeight: 600,
			},
			children,
		};
	};

	return (
		<AppBar
			position="static"
			sx={{
				marginBottom: 2,
				backgroundColor: 'white',
				color: '#016193',
				zIndex: 1000,
				position: 'relative',
			}}
		>
			<Toolbar
				disableGutters
				sx={{
					margin: '0 15px',
					zIndex: 1000,
					'@media (min-width: 600px)': {
						minHeight: '56px',
					},
				}}
			>
				<Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
					<Avatar
						alt="EAS Dashboard"
						src="/img/EAS-logo-new.svg"
						sx={{ display: { xs: 'none', sm: 'flex' }, mr: 1 }}
					/>
					<Box
						sx={{
							flexGrow: 1,
							display: { xs: 'none', sm: 'flex' },
							flexDirection: 'column',
							gap: 0,
							m: 0,
							p: 0,
						}}
					>
						<Typography
							variant="h6"
							noWrap
							component="a"
							href={PATHS.SHOPS_PAGE}
							sx={{
								m: 0,
								mr: 2,
								p: 0,
								fontWeight: 500,
								letterSpacing: '.3rem',
								color: 'inherit',
								textDecoration: 'none',
								fontSize: '.6rem',
								lineHeight: '.9rem',
							}}
						>
							<b>M</b>erchant
						</Typography>
						<Typography
							variant="h6"
							noWrap
							component="a"
							href={PATHS.SHOPS_PAGE}
							sx={{
								m: 0,
								mr: 2,
								p: 0,
								mt: '-2px',
								mb: '-2px',
								fontWeight: 500,
								letterSpacing: '.3rem',
								color: 'inherit',
								textDecoration: 'none',
								fontSize: '.6rem',
								lineHeight: '.9rem',
							}}
						>
							<b>P</b>roduct
						</Typography>

						<Typography
							variant="h6"
							noWrap
							component="a"
							href="/monthly-fee"
							sx={{
								m: 0,
								mr: 2,
								// fontFamily: 'monospace',
								p: 0,
								fontWeight: 500,
								letterSpacing: '.3rem',
								color: 'inherit',
								textDecoration: 'none',
								fontSize: '.6rem',
								lineHeight: '.9rem',
							}}
						>
							<b>M</b>anagement
						</Typography>
					</Box>
				</Box>
				<Box
					sx={{ flexGrow: 1, display: { xs: 'flex', sm: 'none' }, justifyContent: 'space-between' }}
				>
					<IconButton
						size="large"
						aria-label="account of current user"
						aria-controls="menu-appbar"
						aria-haspopup="true"
						onClick={handleOpenNavMenu}
						color="inherit"
					>
						<MenuIcon />
					</IconButton>
					<Menu
						id="menu-appbar"
						anchorEl={anchorElNav}
						anchorOrigin={{
							vertical: 'bottom',
							horizontal: 'left',
						}}
						keepMounted
						transformOrigin={{
							vertical: 'top',
							horizontal: 'left',
						}}
						open={Boolean(anchorElNav)}
						onClose={handleCloseNavMenu}
						sx={{
							display: { xs: 'block', lg: 'none' },
						}}
					>
						<NavigationItems
							isMobile
							handleCloseNavMenu={handleCloseNavMenu}
							location={location}
							navigate={navigate}
						/>
					</Menu>
				</Box>
				<Box sx={{ flexGrow: 1, display: { xs: 'none', sm: 'flex' } }}>
					<NavigationItems
						isMobile={false}
						handleCloseNavMenu={handleCloseNavMenu}
						location={location}
						navigate={navigate}
					/>
				</Box>

				<Box sx={{ flexGrow: 0 }}>
					<Tooltip title="Open settings">
						<IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
							<Avatar
								src={getUserImage(userinfo?.email || '')}
								{...stringAvatar(userinfo?.fullName || '')}
							/>
						</IconButton>
					</Tooltip>
					<UserMenu
						anchorElUser={anchorElUser}
						handleCloseUserMenu={handleCloseUserMenu}
						handleLogout={handleLogout}
					/>
					<MessagesMenu anchorEl={anchorElMessages} handleCloseMenu={handleCloseMessagesMenu} />
				</Box>
			</Toolbar>
		</AppBar>
	);
};
export default ResponsiveAppBar;
